// Centralised HTTP helpers used across the frontend.
//
// Why this exists:
// - The frontend has historically called `r.json()` directly on Fetch
//   responses. That throws "Failed to execute 'json' on 'Response': body
//   stream already read" if anything else touched the body (Netlify SPA
//   fallback returning HTML, a browser extension intercepting the request,
//   Cloudflare error pages, etc.).
// - `safeReadJson()` consumes the body via `.clone().text()` so the source
//   Response is never drained, then attempts JSON.parse defensively.
// - `apiFetch()` is an opinionated convenience wrapper: it prepends the
//   backend base URL, sets `Content-Type: application/json` for POST/PUT/
//   PATCH automatically, and returns the parsed payload (or a structured
//   diagnostic object when the body is empty / not JSON).
//
// All new fetch callers SHOULD use these helpers. Existing call sites can
// migrate gradually.

// Resolve the backend base URL at runtime.
//
// - When running on a self-hosted production domain (geovinum.io, www.geovinum.io)
//   we ALWAYS prefer same-origin so the Netlify `/api/*` rewrite (defined in
//   netlify.toml) forwards calls to the production backend instead of the
//   ephemeral Emergent preview URL that was baked into REACT_APP_BACKEND_URL
//   at build time. Without this, the live site stops working as soon as the
//   Emergent preview pod is stopped (nights, weekends…).
// - On the preview deployment (preview.emergentagent.com) we keep using
//   REACT_APP_BACKEND_URL because there is no same-origin proxy there.
// - On localhost, we also fall back to REACT_APP_BACKEND_URL (or "" if unset).
function resolveApiBase() {
  if (typeof window === "undefined") return process.env.REACT_APP_BACKEND_URL || "";
  const host = (window.location.hostname || "").toLowerCase();
  const isProdHost =
    host === "geovinum.io" ||
    host === "www.geovinum.io" ||
    host.endsWith(".netlify.app");  // any Netlify preview/branch deploy
  if (isProdHost) return "";  // same-origin → handled by netlify.toml /api/* rewrite
  return process.env.REACT_APP_BACKEND_URL || "";
}

const API = resolveApiBase();

/**
 * Read a Response body once, defensively.
 *
 * Returns:
 *   - the parsed JSON payload (object/array), OR
 *   - `{ _empty: true,  status }` if the body is empty, OR
 *   - `{ _raw: <text>, status }` if the body is not valid JSON.
 *
 * Never throws on body-related errors. Errors at the network/transport
 * layer should be caught by the *caller* via try/catch around `fetch()`.
 */
export async function safeReadJson(r) {
  let raw = "";
  try {
    // Clone first so the source Response stays consumable elsewhere.
    raw = await r.clone().text();
  } catch {
    // The clone or text() itself failed — fall through with empty raw.
  }
  if (!raw) return { _empty: true, status: r.status };
  try {
    return JSON.parse(raw);
  } catch {
    return { _raw: raw, status: r.status };
  }
}

/**
 * `apiFetch(path, init)` — opinionated wrapper around `fetch()`.
 *
 * `path` may be:
 *   - "/api/..."  → prefixed with REACT_APP_BACKEND_URL
 *   - full URL    → used as-is
 *
 * Returns `{ ok, status, data, error }` where:
 *   - ok: true if response is HTTP 2xx AND body parsed as JSON with truthy `ok` (or no `ok` field).
 *   - status: HTTP status code.
 *   - data: parsed payload (or `{ _raw, _empty }` diagnostic object).
 *   - error: human-readable string if !ok, else null.
 */
export async function apiFetch(path, init = {}) {
  const url = /^https?:\/\//i.test(path) ? path : `${API}${path}`;
  const method = (init.method || "GET").toUpperCase();
  const headers = { ...(init.headers || {}) };
  // Auto-attach JSON Content-Type when sending a body with no explicit type.
  // Skip for FormData (browser must set its own multipart boundary) and Blob.
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  const isBlob = typeof Blob !== "undefined" && init.body instanceof Blob;
  if (init.body && !isFormData && !isBlob && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  // Auto-attach admin auth so pages that fetch /api/admin/* work
  // without each caller re-plumbing the token. Server accepts Bearer
  // JWT AND X-Admin-Key — we send both and let the backend pick.
  if (!headers.Authorization && !headers.authorization) {
    try {
      const stored = JSON.parse(localStorage.getItem("geovinum.auth.v1") || "null");
      const token = stored?.token;
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        if (!headers["X-Admin-Key"]) headers["X-Admin-Key"] = token;
      }
    } catch { /* ignore */ }
  }
  let r;
  try {
    r = await fetch(url, { ...init, headers, method });
  } catch {
    return { ok: false, status: 0, data: null, error: "Network error — check your connection" };
  }
  const data = await safeReadJson(r);
  // Non-JSON body (HTML SPA fallback, gateway page, …) → bubble up a clear error
  if (data?._empty || data?._raw) {
    return {
      ok: false,
      status: r.status,
      data,
      error: `Server returned an unexpected response (HTTP ${r.status}).`,
    };
  }
  if (!r.ok || (data && data.ok === false)) {
    return {
      ok: false,
      status: r.status,
      data,
      error: data?.detail || data?.error || `HTTP ${r.status}`,
    };
  }
  return { ok: true, status: r.status, data, error: null };
}

export { API };
